import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'
import type { SfJQuery } from '../../src/types'

export default class extends Controller {
  static targets = [
    'addUserBtn',
    'addUserForm',
    'userSelect',
    'allChecksFilter',
    'myChecksFilter',
    'allChecksCount',
    'myChecksCount',
  ]

  // Declare target getters for TypeScript
  declare readonly hasUserSelectTarget: boolean

  // Add property for select2
  private select2Instance: SfJQuery | null = null

  // Define target types with definite assignment
  private addUserBtnTarget!: HTMLElement
  private addUserFormTarget!: HTMLElement
  private userSelectTarget!: HTMLSelectElement
  private allChecksFilterTarget!: HTMLButtonElement
  private myChecksFilterTarget!: HTMLButtonElement
  private allChecksCountTarget!: HTMLElement
  private myChecksCountTarget!: HTMLElement

  connect(): void {
    this.initFilterCounts()
    this.initializeSelect2()
  }

  disconnect(): void {
    this.select2Instance?.off()
  }

  private initializeSelect2(): void {
    if (!this.hasUserSelectTarget) {
      return
    }

    // Cast to our extended jQuery type
    this.select2Instance = $(this.userSelectTarget) as SfJQuery

    this.select2Instance.on('change', (e: JQuery.ChangeEvent) => {
      const target = e.target as HTMLSelectElement
      this.submitAddUsersForm(target.value)
    })

    this.select2Instance.on('select2:close', () => {
      this.hideAddUsersForm()
    })
  }

  private showAddUsersForm(): void {
    if (!this.addUserBtnTarget || !this.addUserFormTarget) return

    this.addUserBtnTarget.style.display = 'none'
    this.addUserFormTarget.style.display = 'block'
    // Focus and open Select2
    this.select2Instance?.select2?.('open')
  }

  private hideAddUsersForm(): void {
    if (!this.addUserBtnTarget || !this.addUserFormTarget) return

    this.addUserBtnTarget.style.display = 'block'
    this.addUserFormTarget.style.display = 'none'
  }

  private submitAddUsersForm(_userId: string): void {
    if (!this.addUserBtnTarget) return

    this.addUserBtnTarget.style.display = 'none'
    const form = this.addUserFormTarget?.querySelector(
      'form'
    ) as HTMLFormElement
    if (form) {
      form.submit()
    }

    window.Silverfin.Spinner.start()
  }

  private initFilterCounts(): void {
    if (!this.allChecksCountTarget || !this.myChecksCountTarget) return

    const allChecks = document.querySelectorAll(
      '[data-behaviour~="ledger-check-list-check"]'
    )

    const myChecks = document.querySelectorAll('[data-my-check="false"]')

    this.allChecksCountTarget.textContent = allChecks.length.toString()
    this.myChecksCountTarget.textContent = myChecks.length.toString()
  }

  private updateMyFilterCount(myCheck: boolean): void {
    if (!this.myChecksCountTarget) return

    let count = parseInt(this.myChecksCountTarget.textContent || '0', 10)
    count = myCheck ? Math.max(0, count - 1) : count + 1

    this.myChecksCountTarget.textContent = count.toString()
  }

  public filterChecks(event: Event): void {
    const target = event.currentTarget as HTMLElement
    const type = (target.dataset.filter as 'all' | 'my') || 'all'
    const checkSelector = '[data-behaviour~="ledger-check-list-check"]'
    const checks = document.querySelectorAll<HTMLElement>(checkSelector)

    if (type === 'my') {
      this.toggleFilter(checks, {
        targetToActivate: this.myChecksFilterTarget,
        targetToDeactive: this.allChecksFilterTarget,
        display: 'none',
      })
    } else {
      this.toggleFilter(checks, {
        targetToActivate: this.allChecksFilterTarget,
        targetToDeactive: this.myChecksFilterTarget,
        display: '',
      })
    }
  }

  private toggleFilter(
    checks: NodeListOf<HTMLElement>,
    {
      targetToActivate,
      targetToDeactive,
      display = 'none',
    }: {
      targetToActivate: HTMLElement | null
      targetToDeactive: HTMLElement | null
      display?: string
    }
  ): void {
    this.activateFilter(targetToActivate)
    this.inactivateFilter(targetToDeactive)
    checks.forEach((check) => {
      const myCheck = check.querySelector('[data-my-check="true"]')
      if (myCheck) {
        check.style.display = display
      }
    })
  }

  private activateFilter(filter: HTMLElement | null): void {
    if (!filter) return

    filter.classList.add('active')
    if (filter instanceof HTMLButtonElement) {
      filter.disabled = true
    }
  }

  private inactivateFilter(filter: HTMLElement | null): void {
    if (!filter) return

    filter.classList.remove('active')
    if (filter instanceof HTMLButtonElement) {
      filter.disabled = false
    }
  }

  // Public method that can be called from outside
  public toggleCheckinOnSuccess(id: string, checkHtml: string): void {
    const temp = document.createElement('div')
    temp.innerHTML = checkHtml.trim()
    const newCheck = temp.firstElementChild as HTMLElement

    if (!newCheck) return

    const myCheck = newCheck.querySelector('[data-my-check="true"]')
    this.updateMyFilterCount(!!myCheck)

    const existingCheck = this.element.querySelector(
      `[data-behaviour~="ledger-check-list-check"][data-id="${id}"]`
    )
    if (existingCheck && existingCheck.parentNode) {
      existingCheck.parentNode.replaceChild(newCheck, existingCheck)
    }

    if (myCheck && this.myChecksFilterTarget?.classList.contains('active')) {
      newCheck.style.display = 'none'
    }
  }
}
